import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Customer } from '../customer/customerType';
import CustomerPaymentApi from './customerPaymentApi';
import { CustomerPayment, ProjectWithInvoices, InvoiceWithPayments } from './customerPaymentType';
import { formatAddress, formatTimestamp, formatDate } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Save, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Alert } from '../components/ui/alert';

// Define allowed payment methods matching database constraints
const PAYMENT_METHODS = ['bank_transfer', 'credit_card', 'cash', 'check', 'money_order'];

// Map database values to user-friendly display labels
const PAYMENT_METHOD_LABELS: { [key: string]: string } = {
  bank_transfer: 'Bank Transfer',
  credit_card: 'Credit Card',
  cash: 'Cash',
  check: 'Check',
  money_order: 'Money Order',
};

const UpdateCustomerPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { project, invoice, customer_payment, customer } = location.state || {};

  const breadcrumbs = [
    { label: 'Customer Payment', path: '/main/customerpayment', state: { customer } },
    { label: 'Edit Payment', path: '' },
  ];

  const [payment, setPayment] = useState<CustomerPayment>({
    customer_payment_id: customer_payment?.customer_payment_id || '',
    invoice_id: customer_payment?.invoice_id || '',
    customer_id: customer_payment?.customer_id || '',
    project_id: customer_payment?.project_id || '',
    amount: customer_payment?.amount || 0,
    payment_date: customer_payment?.payment_date || '',
    payment_method: PAYMENT_METHODS.includes(customer_payment?.payment_method)
      ? customer_payment?.payment_method
      : '',
    note: customer_payment?.note || '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!customer_payment || !project || !invoice) {
      setErrorMessage('Required data is missing. Please navigate from the customer payment page.');
    } else if (customer_payment.payment_method && !PAYMENT_METHODS.includes(customer_payment.payment_method)) {
      setErrorMessage(
        `Invalid payment method: ${customer_payment.payment_method}. Please select a valid option.`
      );
    }
  }, [customer_payment, project, invoice]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPayment((prev) => ({ ...prev, [name]: value }));
  };


  const handleSavePayment = async () => {
    if (!payment.customer_payment_id) {
      setErrorMessage('Payment ID is missing.');
      return;
    }

    if (!payment.payment_method) {
      setErrorMessage('Please select a payment method.');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      await CustomerPaymentApi.updateCustomerPaymentByPaymentId(payment.customer_payment_id, payment);
      setSuccessMessage('Update successful');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'An error occurred while updating the payment.'
      );
    } finally {
      setLoading(false);
    }
  };




  const handleDeletePayment = async () => {
    if (!payment.customer_payment_id) {
      setErrorMessage('Payment ID is missing.');
      return;
    }


    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      await CustomerPaymentApi.deleteCustomerPaymentByPaymentId(payment.customer_payment_id);
      setSuccessMessage('Delete successful');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'An error occurred while updating the payment.'
      );
    } finally {
      setLoading(false);
    }
  };

  const formatCustomerAddress = () =>
    customer
      ? formatAddress(customer.address_1, customer.address_2, customer.city, customer.state, customer.zip)
      : '';

  // Calculate dynamic paid amount and balance
  const calculateDynamicValues = () => {
    if (!invoice || !customer_payment) {
      return {
        totalPaid: 0,
        balanceDue: 0
      };
    }

    const finalTotal = Number(invoice.final_total || 0);
    const originalTotalPaid = Number(invoice.amount_paid || 0);
    const originalPaymentAmount = Number(customer_payment.amount || 0);
    const newPaymentAmount = Number(payment.amount || 0);

    // Calculate new total paid: remove old payment amount, add new payment amount
    const totalPaid = originalTotalPaid - originalPaymentAmount + newPaymentAmount;
    const balanceDue = finalTotal - totalPaid;

    return {
      totalPaid: totalPaid,
      balanceDue: balanceDue
    };
  };

  const { totalPaid, balanceDue } = calculateDynamicValues();

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Edit Customer Payment</h2>

        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
        {successMessage ? (
          <Alert variant="success">{successMessage}</Alert>
        ) : (
          <>
            {customer && (
              <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-white">
                <h3 className="text-base font-semibold text-foreground mb-2">
                  Customer: {customer.first_name} {customer.last_name}
                </h3>
                <div className="text-sm">
                  <span className="font-semibold">Address:</span>{' '}
                  <span className="text-paragraph">{formatCustomerAddress()}</span>
                </div>
              </div>
            )}

            {project && invoice && customer_payment ? (
              <div className="space-y-4">
                {/* Project Information */}
                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <span className="font-semibold">Project Name:</span>{' '}
                  <span className="text-paragraph">{project.name || `Project #${project.project_id}`}</span>
                  {', '}
                  <span className="font-semibold">Description:</span>{' '}
                  <span className="text-paragraph">{project.project_description || 'N/A'}</span>
                  {', '}
                  <span className="font-semibold">Type:</span>{' '}
                  <span className="text-paragraph">{project.project_type || 'N/A'}</span>
                </div>

              {/* Invoice Information */}
              <div className="p-4 bg-white border border-gray-300 rounded-lg mb-6">
                <div className="text-sm space-y-2">
                  <div>
                    <span className="font-semibold">Invoice:</span>{' '}
                    {invoice.name || 'N/A'}
                    {' • '}
                    <span className="font-semibold">ID:</span> {invoice.invoice_id}
                  </div>
                  <div>
                    <span className="font-semibold">Issue Date:</span> {formatDate(invoice.issue_date)}
                    {' • '}
                    <span className="font-semibold">Due Date:</span> {formatDate(invoice.due_date)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t">
                    <div>
                      <span className="text-gray-600">Subtotal:</span>{' '}
                      <span className="font-semibold">${Number(invoice.sub_total_sum_lineitems || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Tax ({Number(invoice.tax_rate || 0).toFixed(2)}%):</span>{' '}
                      <span className="font-semibold">${Number(invoice.tax_amount || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Discount:</span>{' '}
                      <span className="font-semibold text-green-600">-${Number(invoice.discount_usd || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Final Total:</span>{' '}
                      <span className="font-semibold">${Number(invoice.final_total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div>
                      <span className="text-gray-600">Total Paid:</span>{' '}
                      <span className="font-semibold text-green-600">${totalPaid.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Balance Due:</span>{' '}
                      <span className={`font-semibold ${balanceDue <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${balanceDue.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>



                {/* Payment Edit Form */}
                <div className="p-4 border-2 border-gray-400 rounded-lg bg-gray-50">
                  <h3 className="text-base font-semibold text-foreground mb-4">Existing Payment Details</h3>
                  <div className="space-y-4 max-w-sm">
                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount:</Label>
                      <Input
                        type="number"
                        id="amount"
                        name="amount"
                        value={payment.amount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="payment_date">Payment Date:</Label>
                      <Input
                        type="date"
                        id="payment_date"
                        name="payment_date"
                        value={payment.payment_date.split('T')[0]}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="payment_method">Payment Method:</Label>
                      <Select
                        id="payment_method"
                        name="payment_method"
                        value={payment.payment_method}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Method</option>
                        {PAYMENT_METHODS.map((method) => (
                          <option key={method} value={method}>
                            {PAYMENT_METHOD_LABELS[method] || method}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="note">Note:</Label>
                      <Textarea
                        id="note"
                        name="note"
                        value={payment.note || ''}
                        onChange={handleInputChange}
                        className="min-h-[80px] resize-y"
                      />
                    </div>

                    <Button onClick={handleSavePayment} disabled={loading} size="medium" className="mt-3">
                      <Save size={16} />
                      {loading ? 'Saving...' : 'Save Payment'}
                    </Button>
{/** 
                    <Button  variant="inverted" onClick={handleDeletePayment} disabled={loading} size="medium" className="mt-3">
                      <Trash2 size={16} />
                      {loading ? 'Saving...' : 'Delete Payment'}
                    </Button>
*/}

                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 border border-gray-300 rounded-lg bg-white text-center">
                <p className="text-gray-600 italic">No payment data available.</p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default UpdateCustomerPayment;